import { useMemo, useState } from "react"

import { Edit2, Loader2, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import {
  useCreateSessionInvalidation,
  useDeleteSessionInvalidation,
  useSessionInvalidations,
  useUpdateSessionInvalidation,
} from "@/hooks/useSessionInvalidations"
import { useUsers } from "@/hooks/useUsers"
import DashboardLayout from "@/layouts/DashboardLayout"

export default function SessionInvalidations() {
  const { data: invalidations, isLoading } = useSessionInvalidations()
  const { data: usersData } = useUsers({ limit: 1000, isActive: true })
  const createMutation = useCreateSessionInvalidation()
  const updateMutation = useUpdateSessionInvalidation()
  const deleteMutation = useDeleteSessionInvalidation()

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedInvalidation, setSelectedInvalidation] = useState<any>(null)

  // Form state for create
  const [formData, setFormData] = useState({
    user_id: "__all__", // Special value for "All Users"
    invalidate_at: "",
    is_active: true,
    reason: "",
  })

  // Form state for edit
  const [editFormData, setEditFormData] = useState({
    invalidate_at: "",
    is_active: true,
    reason: "",
  })

  const users = useMemo(() => usersData?.users || [], [usersData])

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const formatDateTimeLocal = (dateString: string) => {
    const date = new Date(dateString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    const hours = String(date.getHours()).padStart(2, "0")
    const minutes = String(date.getMinutes()).padStart(2, "0")
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  const handleCreate = async () => {
    if (!formData.invalidate_at) {
      toast.error("Invalidation time is required")
      return
    }

    // Validate that the invalidation time is not in the past
    const invalidateAtDate = new Date(formData.invalidate_at)
    if (invalidateAtDate < new Date()) {
      toast.error("Invalidation time cannot be in the past")
      return
    }

    try {
      await createMutation.mutateAsync({
        user_id: formData.user_id === "__all__" ? null : formData.user_id,
        invalidate_at: invalidateAtDate.toISOString(),
        is_active: formData.is_active,
        reason: formData.reason || null,
      })

      toast.success("Session invalidation created successfully")
      setCreateDialogOpen(false)
      setFormData({
        user_id: "__all__",
        invalidate_at: "",
        is_active: true,
        reason: "",
      })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create session invalidation")
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleEdit = (invalidation: any) => {
    setSelectedInvalidation(invalidation)
    setEditFormData({
      invalidate_at: formatDateTimeLocal(invalidation.invalidate_at),
      is_active: invalidation.is_active,
      reason: invalidation.reason || "",
    })
    setEditDialogOpen(true)
  }

  const handleUpdate = async () => {
    if (!selectedInvalidation) {
      toast.error("No invalidation selected")
      return
    }

    const isPastInvalidation = isPast(selectedInvalidation.invalidate_at)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updateData: any = {
      id: selectedInvalidation.id,
      is_active: editFormData.is_active,
      reason: editFormData.reason || null,
    }

    // Only allow editing time for future invalidations
    if (!isPastInvalidation && editFormData.invalidate_at) {
      const invalidateAtDate = new Date(editFormData.invalidate_at)
      // Validate that the new time is not in the past
      if (invalidateAtDate < new Date()) {
        toast.error("Invalidation time cannot be in the past")
        return
      }
      updateData.invalidate_at = invalidateAtDate.toISOString()
    }

    try {
      await updateMutation.mutateAsync(updateData)

      toast.success("Session invalidation updated successfully")
      setEditDialogOpen(false)
      setSelectedInvalidation(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update session invalidation")
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this session invalidation?")) {
      return
    }

    try {
      await deleteMutation.mutateAsync(id)
      toast.success("Session invalidation deleted successfully")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete session invalidation")
    }
  }

  const getUserName = (userId: string | null) => {
    if (!userId) return "All Users"
    const user = users.find((u) => u.id === userId)
    return user ? `${user.fullName} (${user.email})` : userId
  }

  const isPast = (dateString: string) => {
    return new Date(dateString) < new Date()
  }

  return (
    <DashboardLayout>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Session Invalidations</CardTitle>
              <CardDescription>
                Manage session invalidations to force logout users at specific times. Set user_id to
                null for global invalidation (all users).
              </CardDescription>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              New Invalidation
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <div className="rounded border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Invalidate At</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invalidations && invalidations.length > 0 ? (
                    invalidations.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>
                          <Badge variant={inv.user_id ? "secondary" : "default"}>
                            {getUserName(inv.user_id)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {formatDateTime(inv.invalidate_at)}
                            {isPast(inv.invalidate_at) && (
                              <Badge variant="destructive" className="text-xs">
                                Past
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={inv.is_active ? "default" : "secondary"}>
                            {inv.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className="max-w-[200px] truncate"
                          title={inv.reason || undefined}
                        >
                          {inv.reason || "—"}
                        </TableCell>
                        <TableCell>{formatDateTime(inv.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleEdit(inv)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(inv.id)}
                              disabled={isPast(inv.invalidate_at)}
                              title={
                                isPast(inv.invalidate_at)
                                  ? "Cannot delete past invalidations"
                                  : "Delete invalidation"
                              }
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                        No session invalidations yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Session Invalidation</DialogTitle>
            <DialogDescription>
              Set when sessions should be invalidated. Leave user empty for global invalidation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>User (optional)</Label>
              <Select
                value={formData.user_id}
                onValueChange={(value) => setFormData({ ...formData, user_id: value })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select user (leave empty for all users)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Users</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.fullName} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1">
                Leave empty to invalidate all user sessions
              </div>
            </div>
            <div>
              <Label>Invalidate At *</Label>
              <Input
                className="mt-1"
                type="datetime-local"
                value={formData.invalidate_at}
                onChange={(e) => setFormData({ ...formData, invalidate_at: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                className="mt-1"
                placeholder="e.g., Security incident, Scheduled maintenance"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Active
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Session Invalidation</DialogTitle>
            <DialogDescription>
              Update the invalidation settings. User and time cannot be changed after creation.
            </DialogDescription>
          </DialogHeader>
          {selectedInvalidation && (
            <div className="space-y-4">
              <div>
                <Label>User</Label>
                <div className="mt-1 p-2 bg-muted rounded-md text-sm">
                  {getUserName(selectedInvalidation.user_id)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  User cannot be changed after creation
                </div>
              </div>
              <div>
                <Label>Invalidate At</Label>
                <Input
                  className="mt-1"
                  type="datetime-local"
                  value={editFormData.invalidate_at}
                  disabled={isPast(selectedInvalidation.invalidate_at)}
                  readOnly={isPast(selectedInvalidation.invalidate_at)}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, invalidate_at: e.target.value })
                  }
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {isPast(selectedInvalidation.invalidate_at) &&
                    "Time cannot be changed for past invalidations"}
                </div>
              </div>
              <div>
                <Label>Reason (optional)</Label>
                <Textarea
                  className="mt-1"
                  placeholder="e.g., Security incident, Scheduled maintenance"
                  value={editFormData.reason}
                  onChange={(e) => setEditFormData({ ...editFormData, reason: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit_is_active"
                  checked={editFormData.is_active}
                  onCheckedChange={(checked) =>
                    setEditFormData({ ...editFormData, is_active: checked })
                  }
                />
                <Label htmlFor="edit_is_active" className="cursor-pointer">
                  Active
                </Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
